import React, { useState, useEffect } from 'react';
import { X, Send, Video, Mail, MessageCircle, ExternalLink, Copy, RefreshCw } from 'lucide-react';
import { statusOptions, hasIncompleteData, fetchSIDData, updateSubmissionWithSIDData } from '../../services/submissions.service';
import { listDuplicateSubmissions } from '../../graphql/queries';
import { generateClient } from 'aws-amplify/api';
import SubmissionAppointmentsTab from './SubmissionAppointments';
import StatusHistorySection from './StatusHistorySection';
import { useAuth } from '../../context/AuthContext';

const client = generateClient();
const SubmissionModal = ({ 
  submission, 
  onClose, 
  onStatusChange, 
  onUpdateEligibility,
  onUpdateNotes,
  onVideoLinkUpdate, 
  onSendVideoSMS, 
  onSendVideoEmail, 
  formatDate,
  onOpenSMSConversation,
  onDataRefresh 
}) => {
  // ✅ Local state for notes
  // Local state
  const [activeTab, setActiveTab] = useState('details');
  const [notes, setNotes] = useState(submission.notes || '');
  const [localVideoLink, setLocalVideoLink] = useState(submission.videoLink || '');
  const [recipientNumber, setRecipientNumber] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [isSendingSMS, setIsSendingSMS] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [duplicateSubmissions, setDuplicateSubmissions] = useState([]);
  const [isFetchingSIDData, setIsFetchingSIDData] = useState(false);
  const [sidDataStatus, setSidDataStatus] = useState('');

  // Check if submission has incomplete data
  const isIncomplete = hasIncompleteData(submission);

  // Handle fetching SID data
  const handleFetchSIDData = async () => {
    if (!submission.SID) {
      setError('No SID available for this submission');
      return;
    }

    setIsFetchingSIDData(true);
    setError('');
    setSuccess('');

    try {
      // Fetch data from external API
      const sidData = await fetchSIDData(submission.SID);
      
      if (sidData) {
        // Update the submission with the fetched data
        await updateSubmissionWithSIDData(submission.id, sidData);
        
        setSuccess('SID data fetched and updated successfully!');
        setSidDataStatus('completed');
        
        // Update local submission state with new data
        const updatedSubmission = {
          ...submission,
          age: sidData.age || submission.age,
          gender: sidData.gender || submission.gender,
          zipcode: sidData.zipcode || submission.zipcode,
          state: sidData.state || submission.state,
          householdincome: sidData.householdincome || submission.householdincome,
          transgender: sidData.transgender || submission.transgender,
          userscore: sidData.userscore || submission.userscore
        };
        
        // Call the refresh callback if provided
        if (onDataRefresh) {
          onDataRefresh(updatedSubmission);
        }
      } else {
        setError('No data found for this SID');
        setSidDataStatus('no-data');
      }
    } catch (error) {
      console.error('Error fetching SID data:', error);
      setError('Failed to fetch SID data. Please try again.');
      setSidDataStatus('error');
    } finally {
      setIsFetchingSIDData(false);
    }
  };
  // ✅ Sync local state with `submission.Notes` when switching tabs
  useEffect(() => {
    if (submission) {
      setNotes(submission.Notes || '');
    }
  }, [submission]);

  useEffect(() => {
    const fetchDuplicates = async () => {
      try {
        const response = await client.graphql({
          query: listDuplicateSubmissions,
          variables: {
            filter: { originalSubmissionId: { eq: submission.id } }
          }
        });
        setDuplicateSubmissions(response.data.listDuplicateSubmissions.items || []);
      } catch (err) {
        console.error('Error fetching duplicates:', err);
      }
    };
  
    if (submission?.id) {
      fetchDuplicates();
    }
  }, [submission]);

  // Add auth context to get current user
  const { currentUser } = useAuth();
  
  // Add this constant to specify which user should not see the User Score
  const hideUserScoreForEmail = "mtm2226@cumc.columbia.edu"; // Replace with the actual email
  const shouldHideUserScore = currentUser?.email === hideUserScoreForEmail;

  // Generate random animal name for video link
  function generateRandomAnimalName() {
    const descriptiveWords = [
      'Amazing', 'Brave', 'Clever', 'Dazzling', 'Eager',
      'Fierce', 'Gentle', 'Happy', 'Iconic', 'Jolly',
      'Kind', 'Lively', 'Mighty', 'Noble', 'Optimistic',
      'Peaceful', 'Quick', 'Radiant', 'Silent', 'Tiny',
      'Unique', 'Vibrant', 'Wise', 'Xenial', 'Youthful', 'Zealous',
      'Agile', 'Bold', 'Crafty', 'Delightful', 'Energetic',
      'Fuzzy', 'Graceful', 'Heroic', 'Intelligent', 'Jumpy',
      'Keen', 'Loyal', 'Magical', 'Nimble', 'Observant'
    ];
  
    const animals = [
      'Alligator', 'Badger', 'Cheetah', 'Dolphin', 'Elephant',
      'Fox', 'Giraffe', 'Hippo', 'Iguana', 'Jaguar',
      'Kangaroo', 'Lion', 'Monkey', 'Narwhal', 'Octopus',
      'Panda', 'Quokka', 'Raccoon', 'Shark', 'Tiger',
      'Unicorn', 'Vulture', 'Wolf', 'Xerus', 'Yak', 'Zebra',
      'Antelope', 'Bear', 'Cobra', 'Duck', 'Eagle',
      'Falcon', 'Gazelle', 'Hawk', 'Ibis', 'Jellyfish',
      'Koala', 'Lemur', 'Moose', 'Nightingale', 'Otter'
    ];
  
    // Get random descriptive word and animal
    const randomDescriptive = descriptiveWords[Math.floor(Math.random() * descriptiveWords.length)];
    const randomAnimal = animals[Math.floor(Math.random() * animals.length)];
  
    // Convert to lowercase and hyphenate
    return `${randomDescriptive.toLowerCase()}-${randomAnimal.toLowerCase()}`;
  }

  // Generate video link
  const generateVideoLink = async () => {
    setIsGeneratingLink(true);
    setError('');
    setSuccess('');
    
    try {
      const randomString = generateRandomAnimalName();
      const videoLink = `${process.env.REACT_APP_VIDEO_ROOM_URL}${randomString}`;
      
      // Update local state first
      setLocalVideoLink(videoLink);
      
      // Then update in the database
      await onVideoLinkUpdate(submission.id, videoLink);
      
      setIsGeneratingLink(false);
      setSuccess('Video link generated successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (error) {
      setError('Failed to generate video link. Please try again.');
      setIsGeneratingLink(false);
    }
  };

  // Handle sending SMS
  const handleSendSMS = () => {
    setError('');
    setSuccess('');
    
    // Validate phone number (simple validation for 10 digits)
    const phonePattern = /^\d{10}$/;
    if (recipientNumber && !phonePattern.test(recipientNumber.trim())) {
      setError('Please enter a valid 10-digit mobile number without spaces or special characters.');
      return;
    }
    
    if (!localVideoLink) {
      setError('Please generate a video link first.');
      return;
    }
    
    setIsSendingSMS(true);
    
    // Call the function to send SMS
    if (onSendVideoSMS) {
      onSendVideoSMS(
        submission.mobilenumber, // From number (submission's mobile number)
        recipientNumber, // To number (recipient's mobile number) 
        `Please join me at ${localVideoLink}`
      )
        .then(() => {
          setIsSendingSMS(false);
          setRecipientNumber('');
          setSuccess('SMS sent successfully');
          
          // Clear success message after 3 seconds
          setTimeout(() => {
            setSuccess('');
          }, 3000);
        })
        .catch(error => {
          setError('Failed to send SMS. Please try again.');
          setIsSendingSMS(false);
        });
    } else {
      console.log(`Would send SMS to ${recipientNumber}: Please join me at ${localVideoLink}`);
      setIsSendingSMS(false);
      setRecipientNumber('');
      setSuccess('SMS sent successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    }
  };

  // Handle sending Email
  const handleSendEmail = () => {
    setError('');
    setSuccess('');
    
    if (!recipientEmail) {
      setError('Please enter a valid email address.');
      return;
    }
    
    if (!localVideoLink) {
      setError('Please generate a video link first.');
      return;
    }
    
    setIsSendingEmail(true);
    
    // Call the function to send email
    if (onSendVideoEmail) {
      onSendVideoEmail(
        recipientEmail, // Recipient's email address
        `Video Session Link`,
        `Please join me for a video session`,
        localVideoLink
      )
        .then(() => {
          setIsSendingEmail(false); 
          setRecipientEmail('');
          setSuccess('Email sent successfully');
          
          // Clear success message after 3 seconds
          setTimeout(() => {
            setSuccess('');
          }, 3000);
        })
        .catch(error => {
          setError('Failed to send email. Please try again.');
          setIsSendingEmail(false);
        });
    } else {
      console.log(`Would send email to ${recipientEmail}: Please join me at ${localVideoLink}`);
      setIsSendingEmail(false);
      setRecipientEmail('');
      setSuccess('Email sent successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    }
  };

  /*
  // Handle save notes
  const handleSaveNotes = () => {
    // Add functionality to save notes if needed
    console.log('Saving notes:', notes);
    setSuccess('Notes saved successfully');
    
    // Clear success message after 3 seconds
    setTimeout(() => {
      setSuccess('');
    }, 3000);
  };
 */
  // Handle opening SMS conversation
  const handleOpenSMSConversation = () => {
    onClose();
    if (onOpenSMSConversation) {
      onOpenSMSConversation();
    }
  };

  // Handle copying link to clipboard
  const handleCopyLink = () => {
    navigator.clipboard.writeText(localVideoLink);
    setSuccess('Link copied to clipboard!');
    
    // Clear success message after 3 seconds
    setTimeout(() => {
      setSuccess('');
    }, 3000);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-20 p-4 md:p-0">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <div className="flex items-center">
            <h2 className="text-lg font-semibold text-gray-900">Submission Details</h2>
            <p className="ml-2 text-sm text-gray-500">{submission.mobilenumber}</p>
          </div>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={20} />
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-auto">
          {/* Tabs */}
          <div className="border-b flex">
            <button
              className={`px-4 py-2 text-sm font-medium ${activeTab === 'details' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveTab('details')}
            >
              Details
            </button>
            <button
              className={`px-4 py-2 text-sm font-medium ${activeTab === 'video' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveTab('video')}
            >
              Video
            </button>
            <button
              className={`px-4 py-2 text-sm font-medium ${activeTab === 'status' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveTab('status')}
            >
              Status
            </button>
            <button
              className={`px-4 py-2 text-sm font-medium ${activeTab === 'notes' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveTab('notes')}
            >
              Notes
            </button>
            <button
              className={`px-4 py-2 text-sm font-medium ${activeTab === 'sms' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveTab('sms')}
            >
              SMS
            </button>
            <button
              className={`px-4 py-2 text-sm font-medium ${activeTab === 'appointments' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveTab('appointments')}
            >
              Appointments
            </button>
          </div>
          
          {/* Success and Error Messages */}
          {(success || error) && (
            <div className={`m-4 p-3 rounded-lg ${success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              {success || error}
            </div>
          )}

          {activeTab === 'details' && (
            <div className="p-6">
              {/* SID Data Fetch Section */}
              {isIncomplete && submission.SID && (
                <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-medium text-yellow-800 mb-1">Incomplete Data Detected</h3>
                      <p className="text-sm text-yellow-700">
                        This submission appears to have incomplete data. You can fetch additional data using the SID.
                      </p>
                    </div>
                    <button
                      onClick={handleFetchSIDData}
                      disabled={isFetchingSIDData}
                      className="flex items-center px-4 py-2 bg-yellow-600 hover:bg-yellow-700 disabled:bg-yellow-400 text-white text-sm font-medium rounded-md"
                    >
                      <RefreshCw size={16} className={`mr-2 ${isFetchingSIDData ? 'animate-spin' : ''}`} />
                      {isFetchingSIDData ? 'Fetching...' : 'Fetch SID Data'}
                    </button>
                  </div>
                </div>
              )}

              {/* SID Data Status */}
              {sidDataStatus && (
                <div className={`mb-6 p-4 rounded-lg ${
                  sidDataStatus === 'completed' ? 'bg-green-50 border border-green-200' :
                  sidDataStatus === 'error' ? 'bg-red-50 border border-red-200' :
                  'bg-blue-50 border border-blue-200'
                }`}>
                  <p className={`text-sm ${
                    sidDataStatus === 'completed' ? 'text-green-800' :
                    sidDataStatus === 'error' ? 'text-red-800' :
                    'text-blue-800'
                  }`}>
                    {sidDataStatus === 'completed' ? 'SID data has been successfully fetched and updated.' :
                     sidDataStatus === 'error' ? 'Failed to fetch SID data. Please try again.' :
                     'No data found for this SID.'}
                  </p>
                </div>
              )}

              {/* Original Submission Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Submission Date</h3>
                  <p className="mt-1">{formatDate(submission.submittedDate)}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">ID</h3>
                  <p className="mt-1">{submission.publicId || 'N/A'}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">SID</h3>
                  <p className="mt-1">{submission.SID || 'N/A'}</p>
                </div>
                {!shouldHideUserScore && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">User Score</h3>
                    <p className="mt-1">{submission.userscore || 'N/A'}</p>
                  </div>
                )}
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Age</h3>
                  <p className="mt-1">{submission.age || 'N/A'}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Gender</h3>
                  <p className="mt-1">{submission.gender || 'N/A'}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Transgender</h3>
                  <p className="mt-1">{submission.transgender || 'N/A'}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Zipcode</h3>
                  <p className="mt-1">{submission.zipcode || 'N/A'}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">State</h3>
                  <p className="mt-1">{submission.state || 'N/A'}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Household Income</h3>
                  <p className="mt-1">{submission.householdincome || 'N/A'}</p>
                </div>
              </div>

              {/* Duplicate Submissions Section */}
              {duplicateSubmissions.length > 0 && (
                <div className="mt-8">
                  <h3 className="text-sm font-semibold text-red-700 mb-3">Duplicate Submissions</h3>
                  <div className="space-y-4">
                    {duplicateSubmissions.map((dup) => (
                      <div
                        key={dup.id}
                        className="border border-red-300 bg-red-50 p-4 rounded-md text-sm text-gray-700"
                      >
                        <p><strong>SID:</strong> {dup.SID || 'N/A'}</p>
                        <p><strong>Submission Date:</strong> {formatDate(dup.createdAt)}</p>
                        <p><strong>User Score:</strong> {dup.userscore}</p>
                        <p><strong>Age:</strong> {dup.age}</p>
                        <p><strong>Transgender:</strong> {dup.transgender}</p>
                        <p><strong>Gender:</strong> {dup.gender}</p>
                        <p><strong>Zipcode:</strong> {dup.zipcode}</p>
                        <p><strong>Household Income:</strong> {dup.householdincome}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
      
          {/* Video Tab */}
          {activeTab === 'video' && (
            <div className="p-6 space-y-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Video Session Link</h3>
              
              {localVideoLink ? (
                <div className="space-y-4">
                  {/* Start Session Button */}
                  <button 
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded font-medium w-full flex items-center justify-center"
                    onClick={() => window.open(localVideoLink, '_blank')}
                  >
                    <ExternalLink size={18} className="mr-2" />
                    Start Video Session
                  </button>
                  
                  <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                    <a 
                      href={localVideoLink} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 truncate"
                    >
                      {localVideoLink}
                    </a>
                    <button 
                      className="ml-2 text-blue-600 hover:text-blue-800 flex items-center"
                      onClick={handleCopyLink}
                    >
                      <Copy size={16} className="mr-1" />
                      Copy
                    </button>
                  </div>
                  
                  <div className="border rounded-lg overflow-hidden">
                    <h4 className="bg-gray-50 px-4 py-2 text-sm font-medium">Send via SMS</h4>
                    <div className="p-4 flex flex-col gap-3">
                      <input 
                        type="text" 
                        placeholder="Enter recipient's mobile number (e.g., 1234567890)" 
                        className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={recipientNumber}
                        onChange={(e) => setRecipientNumber(e.target.value)}
                      />
                      <button 
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded font-medium disabled:bg-gray-400 flex items-center justify-center"
                        onClick={handleSendSMS}
                        disabled={isSendingSMS}
                      >
                        <Send size={16} className="mr-1.5" />
                        {isSendingSMS ? 'Sending...' : 'Send SMS'}
                      </button>
                    </div>
                  </div>
                  
                  <div className="border rounded-lg overflow-hidden">
                    <h4 className="bg-gray-50 px-4 py-2 text-sm font-medium">Send via Email</h4>
                    <div className="p-4 flex flex-col gap-3">
                      <input 
                        type="email" 
                        placeholder="Enter recipient's email address" 
                        className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={recipientEmail}
                        onChange={(e) => setRecipientEmail(e.target.value)}
                      />
                      <button 
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-medium disabled:bg-gray-400 flex items-center justify-center"
                        onClick={handleSendEmail}
                        disabled={isSendingEmail || !recipientEmail}
                      >
                        <Mail size={16} className="mr-1.5" />
                        {isSendingEmail ? 'Sending...' : 'Send Email'}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <button 
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-medium w-full disabled:bg-gray-400 flex items-center justify-center"
                  onClick={generateVideoLink}
                  disabled={isGeneratingLink}
                >
                  <Video size={16} className="mr-1.5" />
                  {isGeneratingLink ? 'Generating...' : 'Generate Video Link'}
                </button>
              )}
            </div>
          )}
          
          {/* Status Tab */}
          {activeTab === 'status' && (
            <div className="p-6">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Current Status</h3>
              <div className="mb-6">
                <p className="mb-2 inline-block">
                  <span className={`px-2 py-1 rounded-full ${
                    statusOptions.find(s => s.id === submission.status)?.bgColor || 'bg-yellow-100'
                  } ${
                    statusOptions.find(s => s.id === submission.status)?.textColor || 'text-yellow-800'
                  }`}>
                    {statusOptions.find(s => s.id === submission.status)?.label || 'Submitted'}
                  </span>
                </p>
              </div>
              
              <h3 className="text-sm font-medium text-gray-700 mb-3">Change Status</h3>
              <div className="flex flex-wrap gap-2 mb-6">
                {statusOptions.map(status => (
                  <button 
                    key={status.id}
                    onClick={() => onStatusChange(submission.id, status.id)}
                    className={`px-4 py-2 text-sm rounded-lg ${
                      submission.status === status.id 
                        ? `${status.bgColor} ${status.textColor} border ${status.borderColor}` 
                        : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                    }`}
                  >
                    {status.label}
                  </button>
                ))}
              </div>
              
              {/* Divider */}
              <hr className="my-6 border-gray-200" />
              
              {/* Eligibility Section */}
              <h3 className="text-sm font-medium text-gray-700 mb-3">Eligibility</h3>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="eligibility-checkbox"
                  checked={submission.eligible === 'true'}
                  onChange={(e) => {
                    // Call a function to update eligibility status
                    if (onUpdateEligibility) {
                      onUpdateEligibility(submission.id, e.target.checked ? 'true' : 'false');
                    }
                  }}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="eligibility-checkbox" className="ml-2 block text-sm text-gray-700">
                  Mark as eligible
                </label>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Marking a submission as eligible will allow them to proceed to the next stage of the program.
              </p>

              {/* Status History Section */}
              <StatusHistorySection submissionId={submission.id} />
            </div>
          )}          
          
          {/* Notes Tab */}
          {activeTab === 'notes' && submission && (
            <div className="p-6">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Notes</h3>

              <textarea
                value={notes}  // ✅ Controlled by state
                onChange={(e) => setNotes(e.target.value)}
                rows={10}
                className="w-full border rounded-md p-3 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Add notes about this submission..."
              />
              
              <div className="flex justify-end">
                <button
                  onClick={() => onUpdateNotes(submission.id, notes)}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Save Notes
                </button>
              </div>
            </div>
          )}
          
          {/* SMS Tab */}
          {activeTab === 'sms' && (
            <div className="p-6">
              <h3 className="text-sm font-medium text-gray-700 mb-4">SMS Conversation</h3>
              <p className="text-gray-500 mb-4">
                View and send messages to {submission.mobilenumber}. All messages are logged and stored securely.
              </p>
              
              <button
                onClick={handleOpenSMSConversation}
                className="flex items-center justify-center w-full px-4 py-3 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <MessageCircle size={18} className="mr-2" />
                Open Conversation
              </button>
              
              <div className="mt-6 p-4 bg-blue-50 rounded-md">
                <h4 className="text-sm font-medium text-blue-900 mb-2">About SMS Communications</h4>
                <ul className="text-sm text-blue-800 space-y-1 list-disc pl-5">
                  <li>Messages are received and sent in real-time</li>
                  <li>Conversations are archived for record keeping</li>
                  <li>Standard SMS rates may apply for the recipient</li>
                </ul>
              </div>
            </div>
          )}
           {/* Appointments Tab */}
          {activeTab === 'appointments' && (
            <div className="p-6">
              <SubmissionAppointmentsTab submissionId={submission.id} />
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="border-t p-4 bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubmissionModal;